import { useMutation } from '@tanstack/react-query';
import { Popover } from 'radix-ui';
import { memo, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { isHelpRoute, type HelpRoute } from '@emr/shared';

import { askHelp } from '../api/help.ts';
import { errorMessage } from '../api/client.ts';
import { spotlightTargetFor } from '../help/spotlightTargets.ts';
import { HelpLink } from './HelpLink.tsx';
import { useSpotlight } from './Spotlight.tsx';
import { Button, Field } from './primitives.tsx';

/**
 * The persistent chat affordance (`BACKLOG.md` B6a), mounted once in `shell/AppShell.tsx` — present
 * on every authenticated page, matching how `EnvironmentBanner` is mounted globally in `App.tsx`
 * outside the route tree, adapted one level in since this widget needs a signed-in session
 * (`api/help.ts` calls a route behind the ordinary session gate) and route awareness that only makes
 * sense once inside the shell.
 *
 * **The second compliance risk, made visible rather than silently hoped away.** The task brief is
 * explicit that a user typing PHI into the box themselves is a real, different risk from the
 * context-assembly boundary the server half enforces — the disclaimer below is this feature's
 * answer to it: always shown, not dismissible, directly above the input. It does not *prevent*
 * someone from typing a patient's name; nothing client-side can. What it changes is only what
 * happens to that text after — see `server/src/help/phiPrefilter.ts` (the actual technical
 * mitigation: a message matching a PHI-shaped pattern is refused before it ever reaches Anthropic),
 * `server/src/db/schema/help.ts`'s own header, and `server/src/help/tokenSanitizer.ts` for the rest
 * of this mitigation — and this component's own report for why none of it is a complete solution.
 */
interface ChatTurn {
  readonly id: string;
  readonly role: 'user' | 'assistant';
  readonly text: string;
  readonly matchedTopicIds?: readonly string[];
}

function currentHelpRoute(pathname: string): HelpRoute {
  return isHelpRoute(pathname) ? pathname : '/';
}

/**
 * Owns the transcript and the one thing every caller that appends to it needs done consistently: a
 * stable, monotonically increasing id. Extracted after an independent review noted the
 * `turnCounter.current += 1; setTurns((prev) => [...prev, {...}])` pair was copy-pasted three times
 * in this file (the mutation's `onSuccess`, its `onError`, and the form's `onSubmit`) — one place to
 * get the id generation right rather than three that could quietly drift.
 */
function useChatTurns() {
  const [turns, setTurns] = useState<readonly ChatTurn[]>([]);
  const counter = useRef(0);

  const append = useCallback(
    (role: ChatTurn['role'], text: string, matchedTopicIds?: readonly string[]) => {
      counter.current += 1;
      const id = `${role}-${String(counter.current)}`;
      // Conditionally spread rather than `{ ..., matchedTopicIds }`: `tsconfig.base.json`'s
      // `exactOptionalPropertyTypes` distinguishes "key omitted" from "key present with value
      // `undefined`", and `ChatTurn.matchedTopicIds` is typed as the former, not the latter.
      setTurns((prev) => [
        ...prev,
        { id, role, text, ...(matchedTopicIds !== undefined ? { matchedTopicIds } : {}) },
      ]);
    },
    [],
  );

  return { turns, append };
}

export function HelpChatWidget() {
  const { t } = useTranslation('help');
  const location = useLocation();
  // Destructured to the one field this component actually uses, not the whole `spotlight` object:
  // `useSpotlight()`'s return value is rebuilt whenever the overlay opens or closes
  // (`Spotlight.tsx`'s own `useMemo` includes `active`), but `show` itself is independently stable
  // (a `useCallback` with an empty dependency array in `SpotlightProvider`) — destructuring it here,
  // as a plain local binding, is what lets `react-hooks/exhaustive-deps` (and a reader) see that
  // `handleShowMe` below only actually depends on the stable part, keeping it (and `ChatBubble`,
  // wrapped in `memo`) from being invalidated by an unrelated spotlight-visibility change.
  const { show: showSpotlight } = useSpotlight();
  const { turns, append } = useChatTurns();
  const [draft, setDraft] = useState('');
  const [spotlightNotice, setSpotlightNotice] = useState<string | null>(null);

  const ask = useMutation({
    mutationFn: askHelp,
    onSuccess: (response) => {
      append('assistant', response.answer, response.matchedTopicIds);
    },
    onError: (error) => {
      append('assistant', t('chatWidget.networkError', { error: errorMessage(error) }));
    },
  });

  const handleShowMe = useCallback(
    (topicId: string) => {
      const target = spotlightTargetFor(topicId);
      if (target === null) return;
      // `target.label` is the English fallback baked into `spotlightTargets.ts`'s own closed
      // mapping (a plain data module, no translation context available to it) — overridden here
      // with the translated caption when `help.json`'s `spotlightLabels` has one for this topic.
      const label = t(`spotlightLabels.${topicId}`, { defaultValue: target.label });
      // `show()` is async: it may need to wait a couple of frames while a registered
      // `TargetRevealer` (`components/Spotlight.tsx`) opens something — the mobile nav drawer, for
      // a nav-mapped target — before the element exists to highlight.
      void showSpotlight({ ...target, label }).then((found) => {
        setSpotlightNotice(found ? null : t('chatWidget.notOnPage'));
      });
    },
    [showSpotlight, t],
  );

  return (
    <Popover.Root>
      {/* No `asChild` here, matching `theme/ThemeMenu.tsx`'s own stated reason for avoiding it
          throughout this codebase: `Popover.Trigger` already renders its own `<button>`, styled
          directly rather than wrapped around a second manual `<button>` — the pentest attack
          suite's own login flow caught the nested-button shape live in a real browser (invalid
          HTML, a React hydration warning) the first time this was written the other way. */}
      <Popover.Trigger
        aria-label={t('chatWidget.triggerAriaLabel')}
        className="fixed right-4 bottom-4 z-40 flex items-center gap-2 rounded-pill border border-line bg-accent px-4 py-3 text-sm font-medium text-accent-ink shadow-overlay hover:opacity-90"
      >
        <span aria-hidden="true">?</span>
        {t('chatWidget.triggerLabel')}
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side="top"
          align="end"
          sideOffset={12}
          className="z-40 flex max-h-[min(32rem,80vh)] w-[min(24rem,90vw)] flex-col gap-3 rounded-panel border border-line bg-surface p-4 text-ink shadow-overlay"
        >
          <div>
            <h2 className="m-0 text-base font-semibold text-ink">{t('chatWidget.heading')}</h2>
            <p className="m-0 mt-1 text-xs text-ink-muted">{t('chatWidget.disclaimer')}</p>
          </div>

          <div className="flex min-h-24 flex-1 flex-col gap-3 overflow-y-auto" aria-live="polite">
            {turns.length === 0 ? (
              <p className="m-0 text-sm text-ink-muted">{t('chatWidget.emptyState')}</p>
            ) : (
              turns.map((turn) => <ChatBubble key={turn.id} turn={turn} onShowMe={handleShowMe} />)
            )}
            {ask.isPending ? (
              <p className="m-0 text-sm text-ink-muted">{t('chatWidget.thinking')}</p>
            ) : null}
            {spotlightNotice !== null ? (
              <p role="status" className="m-0 text-sm text-ink-muted">
                {spotlightNotice}
              </p>
            ) : null}
          </div>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              const message = draft.trim();
              if (message === '' || ask.isPending) return;

              append('user', message);
              setDraft('');
              setSpotlightNotice(null);
              ask.mutate({ message, route: currentHelpRoute(location.pathname) });
            }}
            className="flex flex-col gap-2"
          >
            <Field
              label={t('chatWidget.questionLabel')}
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
              }}
              placeholder={t('chatWidget.questionPlaceholder')}
            />
            <Button type="submit" pending={ask.isPending} disabled={draft.trim() === ''}>
              {t('chatWidget.submit')}
            </Button>
          </form>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/**
 * Wrapped in `memo`: every keystroke in the question input re-renders `HelpChatWidget` (the `draft`
 * state it lives in), which would otherwise re-render the entire transcript on each one too. `turn`
 * is a stable object reference from `turns` (only replaced when `useChatTurns`'s `append` actually
 * adds a new one) and `onShowMe` is now a `useCallback`-stabilized reference, so `memo`'s default
 * shallow prop comparison correctly bails out for a bubble whose own props have not changed.
 */
const ChatBubble = memo(function ChatBubble({
  turn,
  onShowMe,
}: {
  readonly turn: ChatTurn;
  readonly onShowMe: (topicId: string) => void;
}) {
  const { t } = useTranslation('help');
  const isUser = turn.role === 'user';
  return (
    <div
      className={
        isUser
          ? 'self-end rounded-control bg-accent-soft px-3 py-2 text-sm text-accent-text'
          : 'self-start rounded-control border border-line bg-canvas px-3 py-2 text-sm text-ink'
      }
    >
      <p className="m-0">{turn.text}</p>
      {turn.matchedTopicIds !== undefined && turn.matchedTopicIds.length > 0 ? (
        <ul className="m-0 mt-2 flex list-none flex-col gap-1 p-0">
          {turn.matchedTopicIds.map((topicId) => (
            <li key={topicId} className="flex items-center gap-2">
              <HelpLink topicId={topicId} />
              {spotlightTargetFor(topicId) !== null ? (
                <button
                  type="button"
                  onClick={() => {
                    onShowMe(topicId);
                  }}
                  className="text-xs font-medium text-accent-text underline underline-offset-2 hover:opacity-80"
                >
                  {t('chatWidget.showMe')}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
});
